
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, DollarSign, FileText, User, AlertTriangle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { LoanStatusBadge } from "@/components/loans/LoanStatusBadge";
import { LoanBasicInfo } from "@/components/loans/details/LoanBasicInfo";
import { LoanTermInfo } from "@/components/loans/details/LoanTermInfo";
import { LoanPaymentInfo } from "@/components/loans/details/LoanPaymentInfo";
import { useToast } from "@/components/ui/use-toast";

// Mock loan data matching the structure in ActiveLoans
const mockLoanDetails = {
  id: "LOAN-2023-001",
  borrower_name: "John Doe",
  borrower_id: "borrower-001",
  amount: 25000,
  interest_rate: 4.5,
  status: "active",
  payment_due_date: "2023-11-15",
  created_at: "2023-10-01T00:00:00Z",
  purpose: "Home Improvement",
  term_months: 36,
  monthly_payment: 742.72,
  total_amount: 26738.06,
  disbursement_date: "2023-10-10T00:00:00Z",
  next_payment_date: "2023-11-15T00:00:00Z",
  risk_score: 78,
  payments: [
    {
      id: "payment-001",
      amount: 742.72,
      payment_date: "2023-11-15T00:00:00Z",
      status: "pending",
    },
    {
      id: "payment-002",
      amount: 742.72,
      payment_date: "2023-12-15T00:00:00Z",
      status: "scheduled",
    },
  ],
  documents: [
    {
      id: "doc-001",
      name: "Loan Agreement",
      date: "2023-10-02T00:00:00Z",
      status: "signed",
    },
    {
      id: "doc-002",
      name: "Income Verification",
      date: "2023-09-25T00:00:00Z",
      status: "verified",
    },
  ],
  notes: [
    {
      id: "note-001",
      date: "2023-10-05T00:00:00Z",
      content: "Borrower called to confirm loan terms.",
      author: "Jane Smith",
    }
  ]
};

const LoanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { data: loan, isLoading } = useQuery({
    queryKey: ['loan-details', id],
    queryFn: async () => {
      try {
        // In a real implementation, this would fetch data from Supabase
        // const { data, error } = await supabase
        //   .from('loan_applications')
        //   .select('*, profiles(*)')
        //   .eq('id', id)
        //   .single();
        
        // For demo purposes, return mock data
        return mockLoanDetails;
      } catch (error) {
        console.error('Error fetching loan details:', error);
        toast({
          title: "Error",
          description: "Failed to load loan details",
          variant: "destructive",
        });
        throw error;
      }
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading loan details...</p>
        </div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/lender/loans/active")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Loans
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Loan Not Found</h2>
            <p className="text-muted-foreground text-center">
              The loan you're looking for couldn't be found or you don't have permission to view its details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getRiskColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-amber-500";
    return "text-red-500";
  };

  const handleApproveLoan = () => {
    toast({
      title: "Loan Approved",
      description: `Loan #${loan.id} has been approved successfully.`,
    });
  };

  const handleRejectLoan = () => {
    toast({
      title: "Loan Rejected",
      description: `Loan #${loan.id} has been rejected.`,
      variant: "destructive",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/lender/loans/active")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Loans
        </Button>
        <div className="flex gap-2">
          {loan.status === 'pending' && (
            <>
              <Button 
                variant="destructive" 
                onClick={handleRejectLoan}
              >
                Reject Loan
              </Button>
              <Button 
                onClick={handleApproveLoan}
              >
                Approve Loan
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-3">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">Loan #{loan.id}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-muted-foreground">Borrower: {loan.borrower_name}</p>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto" 
                    onClick={() => navigate(`/lender/borrowers/${loan.borrower_id}`)}
                  >
                    View Borrower
                  </Button>
                </div>
              </div>
              <LoanStatusBadge status={loan.status} />
            </div>
          </CardContent>
        </Card>

        <div className="md:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>
                <Tabs defaultValue="overview">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="payments">Payments</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                    <TabsTrigger value="notes">Notes</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="pt-4 space-y-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-medium mb-2">Loan Information</h3>
                        <div className="bg-muted p-4 rounded-md">
                          <LoanBasicInfo 
                            purpose={loan.purpose}
                            created_at={loan.created_at}
                            amount={loan.amount}
                          />
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-medium mb-2">Terms</h3>
                        <div className="bg-muted p-4 rounded-md">
                          <LoanTermInfo 
                            interest_rate={loan.interest_rate}
                            term_months={loan.term_months}
                            total_amount={loan.total_amount}
                          />
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-medium mb-2">Payment Information</h3>
                        <div className="bg-muted p-4 rounded-md">
                          <LoanPaymentInfo 
                            monthly_payment={loan.monthly_payment}
                            next_payment_date={loan.next_payment_date}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="payments" className="pt-4">
                    <div className="space-y-4">
                      {loan.payments.length > 0 ? (
                        loan.payments.map((payment) => (
                          <Card key={payment.id}>
                            <CardContent className="p-4 flex justify-between items-center">
                              <div>
                                <p className="font-medium">{new Date(payment.payment_date).toLocaleDateString()}</p>
                                <p className="text-sm text-muted-foreground">
                                  Payment #{payment.id}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  }).format(payment.amount)}
                                </p>
                                <Badge 
                                  variant={payment.status === 'paid' ? 'default' : 
                                          payment.status === 'pending' ? 'outline' : 
                                          'secondary'}
                                >
                                  {payment.status}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-10">
                          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium">No Payments Yet</h3>
                          <p className="text-sm text-muted-foreground">
                            There are no payments for this loan yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="documents" className="pt-4">
                    <div className="space-y-4">
                      {loan.documents.length > 0 ? (
                        loan.documents.map((doc) => (
                          <Card key={doc.id}>
                            <CardContent className="p-4 flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {new Date(doc.date).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <Badge 
                                variant={doc.status === 'signed' || doc.status === 'verified' ? 'default' : 'outline'}
                              >
                                {doc.status}
                              </Badge>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-10">
                          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium">No Documents</h3>
                          <p className="text-sm text-muted-foreground">
                            There are no documents for this loan yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="notes" className="pt-4">
                    <div className="space-y-4">
                      {loan.notes && loan.notes.length > 0 ? (
                        loan.notes.map((note) => (
                          <Card key={note.id}>
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <p className="font-medium">{note.author}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(note.date).toLocaleDateString()}
                                </p>
                              </div>
                              <p className="text-sm">{note.content}</p>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-10">
                          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium">No Notes</h3>
                          <p className="text-sm text-muted-foreground">
                            There are no notes for this loan yet.
                          </p>
                        </div>
                      )}

                      <div className="mt-4">
                        <Button className="w-full">+ Add Note</Button>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Content is now inside TabsContent components */}
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-md font-medium">Loan Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(loan.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interest Rate</span>
                    <span className="font-medium">{loan.interest_rate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Term</span>
                    <span className="font-medium">{loan.term_months} months</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly Payment</span>
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(loan.monthly_payment || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Repayment</span>
                    <span className="font-medium">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(loan.total_amount || 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-md font-medium">Risk Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className={`text-3xl font-bold mb-2 ${getRiskColor(loan.risk_score)}`}>
                      {loan.risk_score}/100
                    </div>
                    <Progress value={loan.risk_score} className="h-2" />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>High Risk</span>
                    <span>Moderate</span>
                    <span>Low Risk</span>
                  </div>
                  
                  <div className="mt-4">
                    <Button className="w-full" variant="outline">
                      View Detailed Risk Analysis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-md font-medium">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: "Message Sent",
                        description: `A message has been sent to ${loan.borrower_name}.`,
                      });
                    }}
                  >
                    <User className="mr-2 h-4 w-4" />
                    Contact Borrower
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: "Download Started",
                        description: "Loan agreement is being downloaded.",
                      });
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Download Agreement
                  </Button>
                  {loan.status === 'active' && (
                    <Button 
                      className="w-full justify-start" 
                      variant="destructive"
                      onClick={() => {
                        toast({
                          title: "Loan Flagged",
                          description: `Loan #${loan.id} has been flagged for review.`,
                          variant: "destructive",
                        });
                      }}
                    >
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Flag for Review
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanDetails;
